/**
 * Organisation details + impact figures shown before disabling.
 */

import React from 'react';
import { Segment, Statistic, Header } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import CurrencyHelper from '../../../../helpers/currencyHelper';
import { OrgDetails, OrgImpact } from '../types';
import { i18nKeys } from '../constants';
import styles from '../DisableOrganization.module.css';

interface OrgDetailsCardProps {
  details: OrgDetails;
  impact: OrgImpact;
}

const formatCreatedAt = (details: OrgDetails): string => {
  if (!details.createdAtLocal) {
    return I18n.t(i18nKeys.DETAILS.NOT_PROVIDED);
  }
  const datePart = details.createdAtLocal.split('T')[0] || details.createdAtLocal;
  return details.timezone ? `${datePart} (${details.timezone})` : datePart;
};

const formatCount = (value: number | null): string =>
  value === null ? I18n.t(i18nKeys.IMPACT.UNAVAILABLE) : String(value);

const OrgDetailsCard: React.FC<OrgDetailsCardProps> = ({ details, impact }) => (
  <>
    <Segment className={styles.detailsSegment}>
      <Header as="h4">
        <Translate value={i18nKeys.DETAILS.TITLE} />
      </Header>
      <div className={styles.detailRow}>
        <strong>
          <Translate value={i18nKeys.DETAILS.NAME} />
        </strong>
        <span>{details.name}</span>
      </div>
      <div className={styles.detailRow}>
        <strong>
          <Translate value={i18nKeys.DETAILS.ID} />
        </strong>
        <span>{details.id}</span>
      </div>
      <div className={styles.detailRow}>
        <strong>
          <Translate value={i18nKeys.DETAILS.CREATED_AT} />
        </strong>
        <span>{formatCreatedAt(details)}</span>
      </div>
      <div className={styles.detailRow}>
        <strong>
          <Translate value={i18nKeys.DETAILS.URL_PATH} />
        </strong>
        <code>{details.urlPath || I18n.t(i18nKeys.DETAILS.NOT_PROVIDED)}</code>
      </div>
      <div className={styles.detailRow}>
        <strong>
          <Translate value={i18nKeys.DETAILS.TAX_ID} />
        </strong>
        <span>{details.taxId || I18n.t(i18nKeys.DETAILS.TAX_ID_NONE)}</span>
      </div>
    </Segment>

    <Segment className={styles.detailsSegment}>
      <Header as="h4">
        <Translate value={i18nKeys.IMPACT.TITLE} />
      </Header>
      <div className={styles.impactGrid}>
        <Statistic size="tiny" className={styles.impactStat}>
          <Statistic.Value>{formatCount(impact.campaignCount)}</Statistic.Value>
          <Statistic.Label>
            <Translate value={i18nKeys.IMPACT.CAMPAIGNS} />
          </Statistic.Label>
        </Statistic>
        <Statistic size="tiny" className={styles.impactStat}>
          <Statistic.Value>{formatCount(impact.donationCount)}</Statistic.Value>
          <Statistic.Label>
            <Translate value={i18nKeys.IMPACT.DONATIONS} />
          </Statistic.Label>
        </Statistic>
        <Statistic size="tiny" className={styles.impactStat}>
          <Statistic.Value>
            {impact.totalRaised === null
              ? I18n.t(i18nKeys.IMPACT.UNAVAILABLE)
              : CurrencyHelper.formatCurrency(impact.totalRaised, impact.currency)}
          </Statistic.Value>
          <Statistic.Label>
            <Translate value={i18nKeys.IMPACT.TOTAL_RAISED} />
          </Statistic.Label>
        </Statistic>
      </div>
      {impact.totalRaisedTruncated && (
        <p className={styles.adminEmail}>
          <Translate value={i18nKeys.IMPACT.TOTAL_RAISED_PARTIAL} />
        </p>
      )}
    </Segment>
  </>
);

export default OrgDetailsCard;
